/**
 * Where to go next, said without a HUD — D-209's layers 2 and 3.
 *
 * `docs/playthrough-audit.md` found the same hole in most beats: the stage
 * knows where she has to be, the player does not, and a patience clock ends
 * the stage around a player who never understood it. A clock is a backstop,
 * not a cue. This is the cue, and it escalates:
 *
 *   1. **She looks.** After `lookAfter` seconds without getting `progressStep`
 *      closer to the stage's goal than she has ever been, she turns to face
 *      it where she stands and her head settles on it, for `lookHold` seconds,
 *      and again every `lookEvery`. It is attention,
 *      never emotion (D-002), and it is what makes the game readable muted —
 *      the spec's definition of done asks for exactly that.
 *   2. **The goal sounds.** After `callAfter`, a sound AT the goal, every
 *      `callEvery`: the room's own draught, field, or bell. Optional per goal,
 *      because layer 1 has to carry the job on its own.
 *
 * Layer 0 — the way on is the lit thing in the opening frame — is authored in
 * each room, not here, and gated by `cold-spawn.spec.ts`.
 *
 * **Progress is best distance, not current distance.** Walking round an
 * obstacle takes her further away for a while, and a stall timer that restarted
 * on every step towards the goal would never fire on a player pacing in front
 * of a shut door. So the clock restarts only on a new best, by a whole step.
 *
 * Pure and fixed-step: no scene, no clock, no audio. `main.ts` feeds it her
 * position and the active goal, reads `attention()` on the render step and
 * drains `calls` into the audio engine.
 */

export interface WayGoal {
  x: number;
  y: number;
  z: number;
  /**
   * Metres. Inside this she has arrived and nothing is said — the stage is now
   * about what she does there, which the verb prompt and her snout already say.
   */
  reach: number;
  /** A registered sound id played at the goal for layer 2, or absent for none. */
  call?: string;
}

export interface WayfindingTuning {
  /** Seconds stalled before her first look. */
  lookAfter: number;
  /** Seconds each look is held. */
  lookHold: number;
  /** Seconds from the start of one look to the start of the next. */
  lookEvery: number;
  /** Seconds stalled before the goal first sounds. */
  callAfter: number;
  /** Seconds between calls. */
  callEvery: number;
  /** Metres closer than her best that counts as progress and restarts the clock. */
  progressStep: number;
  /** 0..1, the perception weight of a look. */
  lookWeight: number;
}

export const WAYFINDING: WayfindingTuning = {
  // WHY: long enough that a player who is looking around on purpose is not
  // told where to go, short enough to land well before any stage's clock.
  lookAfter: 8,
  // WHY: the head reaches ~95 % of its target in ~0.35 s (damping 9); a 2.5 s
  // hold reads as a look rather than a twitch, and ends before it reads as a pose.
  lookHold: 2.5,
  lookEvery: 7,
  // WHY: three looks first. A sound is a bigger statement than a head turn.
  callAfter: 22,
  callEvery: 11,
  // WHY: a metre is about two of her body lengths — below it, turning on the
  // spot and nudging the wall would count as getting somewhere.
  progressStep: 1,
  // WHY: a full-weight focus. Below ~0.6 the 50° neck clamp and the gait's own
  // head bob swallow it from the follow camera.
  lookWeight: 0.9,
};

/**
 * The patience clock a stage with a goal must not undercut. Every stage whose
 * clock ends it before this has ended around a player the game never told —
 * `wayfinding.spec.ts` holds each goal-bearing stage to it.
 */
export function minimumPatience(tuning: WayfindingTuning = WAYFINDING, hasCall = false): number {
  // Two looks, and — where the goal can sound — two calls.
  return hasCall ? tuning.callAfter + tuning.callEvery + 1 : tuning.lookAfter + tuning.lookEvery + tuning.lookHold;
}

export interface WayfindingDebug {
  key: string | null;
  stalled: number;
  best: number;
  looking: boolean;
  looks: number;
  calls: number;
}

export class Wayfinder {
  readonly #tuning: WayfindingTuning;
  #key: string | null = null;
  #goal: WayGoal | null = null;
  #best = Infinity;
  #stalled = 0;
  #nextLook = 0;
  #lookUntil = -1;
  #nextCall = 0;
  #looks = 0;
  #calls = 0;
  #pendingCalls: WayGoal[] = [];

  constructor(tuning: WayfindingTuning = WAYFINDING) {
    this.#tuning = tuning;
  }

  /**
   * The stage's goal. `key` identifies it — beat, part and stage — so the same
   * goal handed in every tick is a no-op and a new stage starts from nothing.
   */
  setGoal(key: string | null, goal: WayGoal | null): void {
    if (key === this.#key) return;
    this.#key = key;
    this.#goal = key === null ? null : goal;
    this.#best = Infinity;
    this.#stalled = 0;
    this.#nextLook = this.#tuning.lookAfter;
    this.#lookUntil = -1;
    this.#nextCall = this.#tuning.callAfter;
  }

  /** Fixed step. `self` is her position this tick. */
  step(dt: number, self: { readonly x: number; readonly z: number }): void {
    const goal = this.#goal;
    if (goal === null || !(dt > 0)) return;
    const distance = Math.hypot(goal.x - self.x, goal.z - self.z);
    if (distance <= goal.reach) {
      // Arrived: say nothing, and start from nothing if she leaves again.
      this.#best = distance;
      this.#restart();
      return;
    }
    if (distance <= this.#best - this.#tuning.progressStep || this.#best === Infinity) {
      this.#best = distance;
      this.#restart();
      return;
    }
    this.#stalled += dt;
    if (this.#stalled >= this.#nextLook) {
      this.#lookUntil = this.#stalled + this.#tuning.lookHold;
      this.#nextLook += this.#tuning.lookEvery;
      this.#looks += 1;
    }
    if (goal.call !== undefined && this.#stalled >= this.#nextCall) {
      this.#nextCall += this.#tuning.callEvery;
      this.#calls += 1;
      this.#pendingCalls.push(goal);
    }
  }

  /** Render step: where her head should go, or null to leave it to everything else. */
  attention(): { x: number; y: number; z: number; kind: 'focus'; weight: number } | null {
    const goal = this.#goal;
    if (goal === null || this.#stalled >= this.#lookUntil) return null;
    return { x: goal.x, y: goal.y, z: goal.z, kind: 'focus', weight: this.#tuning.lookWeight };
  }

  /**
   * Fixed step: the facing to turn her to while a look is live, or null. The
   * head alone cannot carry a look at something behind her — the neck clamps
   * at 50°, and from the follow camera a dark head turning half that far read
   * as nothing (session 71, beat 7's boards). So an idle pig turns round.
   * The controller applies it only with no movement input.
   */
  turnTo(self: { readonly x: number; readonly z: number }): number | null {
    const goal = this.#goal;
    if (goal === null || this.#stalled >= this.#lookUntil) return null;
    return Math.atan2(goal.x - self.x, goal.z - self.z);
  }

  /** The calls raised since the last drain, each the goal it sounds from. */
  drainCalls(): WayGoal[] {
    const drained = this.#pendingCalls;
    this.#pendingCalls = [];
    return drained;
  }

  get debug(): WayfindingDebug {
    return {
      key: this.#key,
      stalled: this.#stalled,
      best: this.#best,
      looking: this.attention() !== null,
      looks: this.#looks,
      calls: this.#calls,
    };
  }

  #restart(): void {
    this.#stalled = 0;
    this.#nextLook = this.#tuning.lookAfter;
    this.#lookUntil = -1;
    this.#nextCall = this.#tuning.callAfter;
  }
}
